/// <reference types="@solidjs/start" />
import { json } from '@solidjs/router';
import { decode } from 'decode-formdata';
import { deepmerge } from 'deepmerge-ts';
import { getRequestEvent, isServer } from 'solid-js/web';
import { ApiError } from './errors.ts';
import { formDataTransform } from './transformer.ts';
import type { IError } from './errors.ts';
import type { FormDataInfo } from 'decode-formdata';

type RequestOptions = {
	url: string;
	method: RequestInit['method'];
	headers?: RequestInit['headers'];
	data?: Record<string, unknown> | URLSearchParams;
	params?: Record<string, unknown> | URLSearchParams;
};

const arrayTypes = [
	'conditions',
	'point',
	'coordinates',
	'flagPoint',
	'difficulty',
	'oneWay',
	'path',
	'alternatePaths',
	'alternatePathsDifficulty',
	'alternatePathsOneWay',
	'permissions',
];

export async function apiClient<T>(opts: RequestOptions): Promise<T> {
	const params = opts.params
		? new URLSearchParams(Object.entries(opts.params).filter(([, v]) => v !== undefined)).toString()
		: '';
	const config = getConfig();

	const init: RequestInit = {
		method: opts.method,
		headers: {
			...config.headers,
			...(Array.isArray(opts.headers)
				? opts.headers.reduce((acc, [key, val]) => ({ ...acc, [key]: val }), {})
				: opts.headers),
		},
		credentials: 'include',
	};

	if (opts.method !== 'GET' && opts.method !== 'HEAD') {
		const formData =
			opts.data instanceof URLSearchParams || opts.data instanceof FormData ? (opts.data as FormData) : null;
		const options: FormDataInfo = { arrays: [] };
		if (formData) {
			for (const key of arrayTypes) {
				if (formData.get(key)) {
					options.arrays!.push(key);
				}
			}
		}
		const body = formData ? decode(formData, options, formDataTransform) : (opts.data ?? {});
		init.body = JSON.stringify(body);
	}

	let response: Response;
	try {
		response = await fetch(`${config.baseUrl}${opts.url}?${params}`, init);
	} catch {
		throw new ApiError({
			error: {
				code: 'service.unavailable',
				message: 'The service is unavailable',
			},
			statusCode: 500,
		});
	}

	if (isServer && response.headers.has('Set-Cookie')) {
		getRequestEvent()?.response.headers.set('Set-Cookie', response.headers.get('Set-Cookie')!);
	}

	if (response.status === 204) {
		return json({}, { revalidate: 'nothing' }) as T;
	}

	if (!response.headers.get('content-type')?.includes('application/json')) {
		return response as T;
	}

	const data = await response.json();

	if (!response.ok || response.status < 200 || response.status >= 300) {
		throw new ApiError(data as IError);
	}

	return json(data, { revalidate: 'nothing' }) as T;
}

type Config = { baseUrl: string; headers: Record<string, string> };

const baseConfig: Config = Object.freeze({ baseUrl: '', headers: {} });
apiClient.config = { ...baseConfig } satisfies Config;

export function updateApiClientConfig(config: Partial<Config>): Config {
	if (isServer) {
		const event = getRequestEvent();

		if (event) {
			event.locals ??= { apiClientConfig: { ...baseConfig } };
			event.locals.apiClientConfig = deepmerge(baseConfig, event.locals.apiClientConfig, config);
			return event.locals.apiClientConfig;
		}
	}
	apiClient.config = deepmerge(apiClient.config, config);
	return apiClient.config;
}

export function getConfig(): Config {
	if (isServer) {
		const event = getRequestEvent();
		return event?.locals?.apiClientConfig ?? apiClient.config;
	}

	return apiClient.config;
}
